import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Plus,
  Search,
  Pencil,
  Trash2,
  Loader2,
  Globe,
  MapPin,
  Building2,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { CountryFormPanel, CityFormPanel } from '../components/locations/LocationFormPanel';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// ==================== COUNTRIES TAB ====================

const CountriesTab = ({ t }) => {
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  useEffect(() => {
    fetchCountries();
  }, [search]);

  const fetchCountries = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      const response = await axios.get(`${API_URL}/api/locations/countries?${params.toString()}`);
      setCountries(response.data);
    } catch (error) {
      console.error('Error fetching countries:', error);
      toast.error(t('messages.errorLoadingCountries'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (data) => {
    try {
      await axios.post(`${API_URL}/api/locations/countries`, data);
      toast.success(t('messages.countryCreated'));
      fetchCountries();
    } catch (error) {
      const message = error.response?.data?.detail;
      toast.error(typeof message === 'string' ? message : t('messages.errorCreatingCountry'));
      throw error;
    }
  };

  const handleUpdate = async (data) => {
    try {
      await axios.put(`${API_URL}/api/locations/countries/${editingItem.id}`, data);
      toast.success(t('messages.countryUpdated'));
      fetchCountries();
    } catch (error) {
      const message = error.response?.data?.detail;
      toast.error(typeof message === 'string' ? message : t('messages.errorUpdatingCountry'));
      throw error;
    }
  };

  const handleDelete = async () => {
    try {
      const response = await axios.delete(`${API_URL}/api/locations/countries/${itemToDelete.id}`);
      toast.success(response.data.message || t('messages.countryDeleted'));
      fetchCountries();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('messages.errorDeletingCountry'));
    } finally {
      setDeleteDialogOpen(false);
      setItemToDelete(null);
    }
  };

  const closeForm = () => {
    setIsFormOpen(false);
    setEditingItem(null);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder={t('countries.searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="country-search"
          />
        </div>
        <Button 
          className="bg-[#E63946] hover:bg-[#C92A36]"
          onClick={() => setIsFormOpen(true)}
          data-testid="add-country-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          {t('countries.newCountry')}
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-48">
          <Loader2 className="h-8 w-8 animate-spin text-[#E63946]" />
        </div>
      ) : countries.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <Globe className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-1">{t('countries.noCountries')}</h3>
            <p className="text-gray-500 dark:text-gray-400 mb-4">{t('countries.createFirst')}</p>
            <Button className="bg-[#E63946] hover:bg-[#C92A36]" onClick={() => setIsFormOpen(true)}>
              <Plus className="h-4 w-4 mr-2" /> {t('countries.createCountry')}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700" data-testid="countries-table">
              <thead>
                <tr>
                  <th className="px-4 py-3 bg-gray-50 dark:bg-gray-800 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('countries.table.country')}</th>
                  <th className="px-4 py-3 bg-gray-50 dark:bg-gray-800 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('countries.table.code')}</th>
                  <th className="px-4 py-3 bg-gray-50 dark:bg-gray-800 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('countries.table.phone')}</th>
                  <th className="px-4 py-3 bg-gray-50 dark:bg-gray-800 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('countries.table.currency')}</th>
                  <th className="px-4 py-3 bg-gray-50 dark:bg-gray-800 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('countries.table.cities')}</th>
                  <th className="px-4 py-3 bg-gray-50 dark:bg-gray-800 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('countries.table.status')}</th>
                  <th className="px-4 py-3 bg-gray-50 dark:bg-gray-800 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('countries.table.actions')}</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                {countries.map((country) => (
                  <tr key={country.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Globe className="h-4 w-4 text-[#E63946]" />
                        <span className="font-medium">{country.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant="outline">{country.code}</Badge>
                    </td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{country.phone_code || '-'}</td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                      {country.currency ? `${country.currency_symbol || ''} ${country.currency}` : '-'}
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant="secondary">{t('countries.table.citiesCount', { count: country.cities_count })}</Badge>
                    </td>
                    <td className="px-4 py-3">
                      <Badge className={country.status === 'active' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'}>
                        {country.status === 'active' ? t('status.active') : t('status.inactive')}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => { setEditingItem(country); setIsFormOpen(true); }}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" className="text-red-500" onClick={() => { setItemToDelete(country); setDeleteDialogOpen(true); }}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Country Form Panel */}
      <CountryFormPanel
        country={editingItem}
        isOpen={isFormOpen}
        onClose={closeForm}
        onSubmit={editingItem ? handleUpdate : handleCreate}
      />

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-red-500" />
              {t('countries.deleteConfirm.title')}
            </DialogTitle>
            <DialogDescription>
              {t('countries.deleteConfirm.message', { name: itemToDelete?.name })}
              {itemToDelete?.cities_count > 0 && (
                <span className="block mt-2 text-orange-600">
                  {t('countries.deleteConfirm.hasCities', { count: itemToDelete.cities_count })}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>{t('common.cancel')}</Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-country">{t('common.delete')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==================== CITIES TAB ====================

const CitiesTab = ({ countries, t }) => {
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [countryFilter, setCountryFilter] = useState('all');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  useEffect(() => {
    fetchCities();
  }, [search, countryFilter]);

  const fetchCities = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (countryFilter && countryFilter !== 'all') params.append('country_id', countryFilter);
      const response = await axios.get(`${API_URL}/api/locations/cities?${params.toString()}`);
      setCities(response.data);
    } catch (error) {
      console.error('Error fetching cities:', error);
      toast.error(t('messages.errorLoadingCities'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (data) => {
    try {
      await axios.post(`${API_URL}/api/locations/cities`, data);
      toast.success(t('messages.cityCreated'));
      fetchCities();
    } catch (error) {
      const message = error.response?.data?.detail;
      toast.error(typeof message === 'string' ? message : t('messages.errorCreatingCity'));
      throw error;
    }
  };

  const handleUpdate = async (data) => {
    try {
      await axios.put(`${API_URL}/api/locations/cities/${editingItem.id}`, data);
      toast.success(t('messages.cityUpdated'));
      fetchCities();
    } catch (error) {
      const message = error.response?.data?.detail;
      toast.error(typeof message === 'string' ? message : t('messages.errorUpdatingCity'));
      throw error;
    }
  };

  const handleDelete = async () => {
    try {
      const response = await axios.delete(`${API_URL}/api/locations/cities/${itemToDelete.id}`);
      toast.success(response.data.message || t('messages.cityDeleted'));
      fetchCities();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('messages.errorDeletingCity'));
    } finally {
      setDeleteDialogOpen(false);
      setItemToDelete(null);
    }
  };

  const closeForm = () => {
    setIsFormOpen(false);
    setEditingItem(null);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="flex flex-1 gap-4 max-w-2xl">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder={t('cities.searchPlaceholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              data-testid="city-search"
            />
          </div>
          <Select value={countryFilter} onValueChange={setCountryFilter}>
            <SelectTrigger className="w-48" data-testid="city-country-filter">
              <SelectValue placeholder={t('cities.filterByCountry')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('cities.allCountries')}</SelectItem>
              {countries.map((country) => (
                <SelectItem key={country.id} value={country.id}>{country.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button 
          className="bg-[#E63946] hover:bg-[#C92A36]"
          onClick={() => setIsFormOpen(true)}
          data-testid="add-city-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          {t('cities.newCity')}
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-48">
          <Loader2 className="h-8 w-8 animate-spin text-[#E63946]" />
        </div>
      ) : cities.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <Building2 className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-1">{t('cities.noCities')}</h3>
            <p className="text-gray-500 dark:text-gray-400 mb-4">{t('cities.createFirst')}</p>
            <Button className="bg-[#E63946] hover:bg-[#C92A36]" onClick={() => setIsFormOpen(true)}>
              <Plus className="h-4 w-4 mr-2" /> {t('cities.createCity')}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700" data-testid="cities-table">
              <thead>
                <tr>
                  <th className="px-4 py-3 bg-gray-50 dark:bg-gray-800 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('cities.table.city')}</th>
                  <th className="px-4 py-3 bg-gray-50 dark:bg-gray-800 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('cities.table.country')}</th>
                  <th className="px-4 py-3 bg-gray-50 dark:bg-gray-800 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('cities.table.department')}</th>
                  <th className="px-4 py-3 bg-gray-50 dark:bg-gray-800 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('cities.table.postalCode')}</th>
                  <th className="px-4 py-3 bg-gray-50 dark:bg-gray-800 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('cities.table.status')}</th>
                  <th className="px-4 py-3 bg-gray-50 dark:bg-gray-800 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('cities.table.actions')}</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                {cities.map((city) => (
                  <tr key={city.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-[#E63946]" />
                        <span className="font-medium">{city.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{city.country_name}</td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{city.state_department || '-'}</td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{city.postal_code || '-'}</td>
                    <td className="px-4 py-3">
                      <Badge className={city.status === 'active' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'}>
                        {city.status === 'active' ? t('status.active') : t('status.inactive')}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => { setEditingItem(city); setIsFormOpen(true); }}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" className="text-red-500" onClick={() => { setItemToDelete(city); setDeleteDialogOpen(true); }}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* City Form Panel */}
      <CityFormPanel
        city={editingItem}
        countries={countries}
        isOpen={isFormOpen}
        onClose={closeForm}
        onSubmit={editingItem ? handleUpdate : handleCreate}
      />

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-red-500" />
              {t('cities.deleteConfirm.title')}
            </DialogTitle>
            <DialogDescription>
              {t('cities.deleteConfirm.message', { name: itemToDelete?.name })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>{t('common.cancel')}</Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-city">{t('common.delete')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==================== MAIN PAGE ====================

export const LocationsPage = () => {
  const { t } = useTranslation('locations');
  const [countries, setCountries] = useState([]);
  const [activeTab, setActiveTab] = useState('countries');

  useEffect(() => {
    fetchCountries();
  }, []);

  const fetchCountries = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/locations/countries`);
      setCountries(response.data);
    } catch (error) {
      console.error('Error fetching countries:', error);
    }
  };

  return (
    <div className="space-y-6" data-testid="locations-page">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 font-['Work_Sans',sans-serif]">
          {t('title')}
        </h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1">
          {t('subtitle')}
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="countries" className="flex items-center gap-2" data-testid="tab-countries">
            <Globe className="h-4 w-4" />
            {t('tabs.countries')}
          </TabsTrigger>
          <TabsTrigger value="cities" className="flex items-center gap-2" data-testid="tab-cities">
            <Building2 className="h-4 w-4" />
            {t('tabs.cities')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="countries" className="mt-4">
          <CountriesTab t={t} />
        </TabsContent>

        <TabsContent value="cities" className="mt-4">
          <CitiesTab countries={countries} t={t} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default LocationsPage;
